import { useState, useEffect, useRef, useCallback } from 'react';
import { useProjectStore } from '../../store/useProjectStore';
import { useSSEStore, type SkillProgress } from '../../store/useSSEStore';
import { usePdfRenderer } from '../../hooks/usePdfRenderer';
import { pdfUrl } from '../../api/endpoints';
import * as api from '../../api/endpoints';
import { withProjectQuery } from '../../api/projectContext';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import type {
  ResearchReport, FileNameMapping, ResearchZone,
  SurveyorAlert, BoqSectionAnalysis, BoqKeyItem, PageRef,
  PageClassification, ScopePageSummary,
} from '../../types';

export function ResearchTab() {
  const report = useProjectStore((s) => s.researchReport);
  const triage = useProjectStore((s) => s.triage);
  const navTarget = useProjectStore((s) => s.pdfNavigationTarget);
  const navigateToPage = useProjectStore((s) => s.navigateToPage);

  // PDF viewer state
  const [viewerFile, setViewerFile] = useState<string | null>(null);
  const [viewerPage, setViewerPage] = useState(1);
  const [viewerMaxPage, setViewerMaxPage] = useState(1);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { renderPage, loadPdf } = usePdfRenderer();

  // Consume navigation target
  useEffect(() => {
    if (navTarget) {
      setViewerFile(navTarget.file);
      setViewerPage(navTarget.page);
      // Clear the target so it doesn't re-trigger
      useProjectStore.setState({ pdfNavigationTarget: null });
    }
  }, [navTarget]);

  // Render PDF page when viewer state changes
  const renderCurrentPage = useCallback(async () => {
    if (!viewerFile || !canvasRef.current) return;
    const url = pdfUrl(viewerFile);
    const doc = await loadPdf(url);
    if (doc) setViewerMaxPage(doc.numPages);
    await renderPage(canvasRef.current, url, viewerPage);
  }, [viewerFile, viewerPage, renderPage, loadPdf]);

  useEffect(() => {
    renderCurrentPage();
  }, [renderCurrentPage]);

  const handlePageNav = (file: string, page: number) => {
    setViewerFile(file);
    setViewerPage(page);
  };

  const showViewer = viewerFile !== null;

  // Build scope pages from triage
  const scopePages = buildScopePages(triage?.classifications);

  const content = (
    <div className="research-scroll-content">
      <PublishProgressBanner />
      <ResearchGateBanner />
      <SurveyorAlerts report={report} onNavigate={handlePageNav} />
      <SurveyorBrief report={report} triage={triage} />
      <MissingDocuments report={report} />
      <ScopeSummarySection summary={report?.scope_summary} onNavigate={handlePageNav} />
      <ScopePages pages={scopePages} onNavigate={handlePageNav} />
      <BoqSections report={report} onNavigate={handlePageNav} />
      <FileInventory report={report} />
      <ZoneCards report={report} />
      <QualityNotes report={report} />
    </div>
  );

  return (
    <PanelGroup direction="horizontal">
      <Panel defaultSize={showViewer ? 50 : 100} minSize={30}>
        <div className="research-container">
          <div className="research-sidebar">
            <ProjectOverview report={report} />
            <PageTypes report={report} />
          </div>
          <div className="research-main">
            {content}
          </div>
        </div>
      </Panel>
      {showViewer && (
        <>
          <PanelResizeHandle className="split-divider" />
          <Panel minSize={25}>
            <div className="research-pdf-viewer">
              <div className="page-nav">
                <button
                  className="nav-btn"
                  disabled={viewerPage <= 1}
                  onClick={() => setViewerPage((p) => p - 1)}
                >
                  &larr;
                </button>
                <span className="page-info">
                  {viewerFile} — p.{viewerPage} / {viewerMaxPage}
                </span>
                <button
                  className="nav-btn"
                  disabled={viewerPage >= viewerMaxPage}
                  onClick={() => setViewerPage((p) => p + 1)}
                >
                  &rarr;
                </button>
                <button
                  className="nav-btn close-btn"
                  onClick={() => setViewerFile(null)}
                  title="Close viewer"
                >
                  &times;
                </button>
              </div>
              <div className="pdf-container">
                <canvas ref={canvasRef} />
              </div>
            </div>
          </Panel>
        </>
      )}
    </PanelGroup>
  );
}

// ── Research Gate Banner ──────────────────────────────────────────

function ResearchGateBanner() {
  const pendingApproval = useSSEStore((s) => s.pendingApproval);
  const report = useProjectStore((s) => s.researchReport);
  const triage = useProjectStore((s) => s.triage);
  const separation = useProjectStore((s) => s.separation);

  if (!pendingApproval) return null;
  const skillName = pendingApproval.skill_name || pendingApproval.skill || '';
  if (!skillName.includes('research')) return null;

  const handleContinue = async () => {
    await api.approve(pendingApproval.request_id);
    useSSEStore.getState().setPendingApproval(null);
  };

  const handleStop = async () => {
    await api.reject(pendingApproval.request_id);
    useSSEStore.getState().setPendingApproval(null);
  };

  // Compute stats
  const totalPages = triage?.classifications?.length || 0;
  const zoneCount = separation?.zones?.length || 0;
  const boardCount = report?.distribution_boards?.length || 0;

  return (
    <div className="research-gate-banner">
      <div className="gate-banner-top">
        <span className="research-gate-badge">RESEARCH COMPLETE</span>
        <span className="gate-step-indicator">Step 2 of 3</span>
      </div>
      {(totalPages > 0 || zoneCount > 0 || boardCount > 0) && (
        <div className="gate-stats-row">
          {totalPages > 0 && <span className="gate-stat">{totalPages} pages analyzed</span>}
          {zoneCount > 0 && <span className="gate-stat">{zoneCount} zones</span>}
          {boardCount > 0 && <span className="gate-stat">{boardCount} dist. boards</span>}
        </div>
      )}
      <div className="gate-banner-body">
        Review the findings above. When ready, extraction will process diagrams automatically.
      </div>
      <div className="gate-actions">
        <button className="btn-continue btn-continue-primary" onClick={handleContinue}>
          Continue to Extraction →
        </button>
        <button className="btn-stop" onClick={handleStop}>
          Stop Here
        </button>
      </div>
    </div>
  );
}

// ── Page ref link ─────────────────────────────────────────────────

function PageRefLink({
  pageRef,
  onNavigate,
}: {
  pageRef: PageRef;
  onNavigate: (file: string, page: number) => void;
}) {
  // Skip invalid references (N/A, empty file, or page <= 0)
  if (!pageRef.file || pageRef.file === 'N/A' || pageRef.page <= 0) return null;

  return (
    <button
      className="page-ref-link"
      onClick={() => onNavigate(pageRef.file, pageRef.page)}
      title={`${pageRef.file} p.${pageRef.page}${pageRef.position_number ? ` — #${pageRef.position_number}` : ''}`}
    >
      p.{pageRef.page}
      {pageRef.position_number && <span className="text-dim"> #{pageRef.position_number}</span>}
    </button>
  );
}

// ── Scope Summary ─────────────────────────────────────────────────

function ScopeSummarySection({
  summary,
  onNavigate,
}: {
  summary?: ScopePageSummary | null;
  onNavigate: (file: string, page: number) => void;
}) {
  if (!summary) return null;

  const sections: { label: string; value?: string; items?: string[] }[] = [
    { label: 'Summary', value: summary.summary },
    { label: 'Scope of Work', value: summary.scope_of_work },
    { label: 'Special Conditions', items: summary.special_conditions },
    { label: 'Material Requirements', items: summary.material_requirements },
    { label: 'Standards & Regulations', items: summary.standards_and_regulations },
    { label: 'Warranty & Liability', value: summary.warranty_and_liability },
    { label: 'Timeline & Milestones', value: summary.timeline_and_milestones },
    { label: 'Key Quantities', items: summary.key_quantities },
  ];

  const hasSurveyorNotes = summary.surveyor_notes && summary.surveyor_notes.length > 0;

  return (
    <div className="research-section">
      <h3>
        Project Description Summary
        {summary.source_pages && summary.source_pages.length > 0 && (
          <span className="section-count-badge">{summary.source_pages.length} pages</span>
        )}
      </h3>
      <div className="scope-summary-card">
        {sections.map((s) => {
          if (s.value) {
            return (
              <div key={s.label} className="scope-summary-field">
                <div className="scope-summary-label">{s.label}</div>
                <div className="scope-summary-value">{s.value}</div>
              </div>
            );
          }
          if (s.items && s.items.length > 0) {
            return (
              <div key={s.label} className="scope-summary-field">
                <div className="scope-summary-label">{s.label}</div>
                <ul className="scope-summary-list">
                  {s.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            );
          }
          return null;
        })}

        {hasSurveyorNotes && (
          <div className="scope-surveyor-notes">
            <div className="scope-summary-label">Surveyor Notes</div>
            {summary.surveyor_notes!.map((note, i) => (
              <div key={i} className="scope-surveyor-note">{note}</div>
            ))}
          </div>
        )}

        {summary.source_pages && summary.source_pages.length > 0 && (
          <div className="scope-source-pages">
            <span className="scope-summary-label">Source: </span>
            {summary.source_pages.map((sp, i) => (
              <button
                key={i}
                className="page-ref-link"
                onClick={() => onNavigate(sp.file, sp.page)}
                title={`${sp.file} p.${sp.page} (${sp.doc_type})`}
              >
                p.{sp.page}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Scope Pages ───────────────────────────────────────────────────

interface ScopePage {
  file: string;
  page: number;
  doc_type: string;
  title?: string;
}

interface ScopeGroup {
  label: string;
  doc_type: string;
  pages: ScopePage[];
  surveyorHint: string;
}

const SCOPE_DOC_TYPES: Record<string, { label: string; hint: string }> = {
  cover: {
    label: 'Cover / Title Pages',
    hint: 'Check project name, client, contractor, revision dates, and project number',
  },
  title_page: {
    label: 'Title Pages',
    hint: 'Verify drawing index, revision status, and applicable standards references',
  },
  general_notes: {
    label: 'General Notes & Specifications',
    hint: 'Critical: installation standards, cable requirements, grounding specs, special conditions',
  },
  table_of_contents: {
    label: 'Table of Contents',
    hint: 'Use to verify document completeness — cross-check against available drawings',
  },
  layout_plan: {
    label: 'Layout Plans',
    hint: 'Count fixtures, sockets, switches per room. Verify zone coverage matches SLDs',
  },
  detail_drawing: {
    label: 'Detail Drawings',
    hint: 'Check mounting details, special installations, and non-standard requirements',
  },
  cable_schedule: {
    label: 'Cable Schedules',
    hint: 'Cross-reference cable types and lengths with SLD specifications',
  },
};

function buildScopePages(classifications?: PageClassification[]): ScopeGroup[] {
  if (!classifications) return [];

  const groups: Record<string, ScopePage[]> = {};
  for (const c of classifications) {
    const info = SCOPE_DOC_TYPES[c.doc_type];
    if (!info) continue;
    if (!groups[c.doc_type]) groups[c.doc_type] = [];
    groups[c.doc_type].push({
      file: c.file,
      page: c.page,
      doc_type: c.doc_type,
      title: c.title,
    });
  }

  return Object.entries(groups).map(([docType, pages]) => {
    const info = SCOPE_DOC_TYPES[docType];
    return {
      label: info.label,
      doc_type: docType,
      pages,
      surveyorHint: info.hint,
    };
  });
}

function ScopePages({
  pages,
  onNavigate,
}: {
  pages: ScopeGroup[];
  onNavigate: (file: string, page: number) => void;
}) {
  if (pages.length === 0) return null;

  const totalPages = pages.reduce((sum, g) => sum + g.pages.length, 0);

  return (
    <div className="research-section">
      <h3>
        Project Scope Documents
        <span className="section-count-badge">{totalPages} pages</span>
      </h3>
      <div className="scope-groups">
        {pages.map((g) => (
          <div key={g.doc_type} className="scope-group">
            <div className="scope-group-header">
              <span className="scope-group-label">{g.label}</span>
              <span className="scope-group-count">{g.pages.length}</span>
            </div>
            <div className="scope-group-hint">{g.surveyorHint}</div>
            <div className="scope-page-refs">
              {g.pages.map((p, i) => (
                <button
                  key={i}
                  className="page-ref-link"
                  onClick={() => onNavigate(p.file, p.page)}
                  title={`${p.file} p.${p.page}${p.title ? ` — ${p.title}` : ''}`}
                >
                  p.{p.page}
                  {p.title && <span className="text-dim"> {p.title}</span>}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Surveyor Alerts ───────────────────────────────────────────────

function SurveyorAlerts({
  report,
  onNavigate,
}: {
  report: ResearchReport | null;
  onNavigate: (file: string, page: number) => void;
}) {
  const alerts = report?.surveyor_alerts;
  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="research-section">
      <h3>Surveyor Alerts</h3>
      <div className="surveyor-alerts">
        {alerts.map((a: SurveyorAlert, i: number) => (
          <div key={i} className={`surveyor-alert alert-${a.severity}`}>
            <span className={`alert-severity-badge severity-${a.severity}`}>
              {a.severity}
            </span>
            <span className="alert-message">{a.message}</span>
            {a.references && a.references.length > 0 && (
              <span className="alert-refs">
                {a.references.map((r, j) => (
                  <PageRefLink key={j} pageRef={r} onNavigate={onNavigate} />
                ))}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── BOQ Section Analysis ──────────────────────────────────────────

function BoqSections({
  report,
  onNavigate,
}: {
  report: ResearchReport | null;
  onNavigate: (file: string, page: number) => void;
}) {
  const sections = report?.boq_sections;
  if (!sections || sections.length === 0) return null;

  return (
    <div className="research-section">
      <h3>
        BoQ Section Analysis
        {report?.boq_total_positions != null && report.boq_total_positions > 0 && (
          <span className="section-count-badge">{report.boq_total_positions} positions</span>
        )}
      </h3>
      <div className="boq-section-cards">
        {sections.map((s: BoqSectionAnalysis) => (
          <BoqSectionCard
            key={`${s.section_number}-${s.section_title}`}
            section={s}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
}

function BoqSectionCard({
  section,
  onNavigate,
}: {
  section: BoqSectionAnalysis;
  onNavigate: (file: string, page: number) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`boq-section-card importance-border-${section.importance}`}>
      <div className="boq-section-header" onClick={() => setExpanded(!expanded)}>
        <div className="boq-section-title-row">
          <span className="boq-section-number">{section.section_number}</span>
          <span className="boq-section-title">{section.section_title || '(Unclassified)'}</span>
          <span className={`importance-badge importance-${section.importance}`}>
            {section.importance}
          </span>
        </div>
        <div className="boq-section-meta">
          <span>{section.position_count} positions</span>
          {section.page_range && section.page_range.length === 2 && section.file && (
            <button
              className="page-ref-link"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(section.file!, section.page_range![0]);
              }}
            >
              pp. {section.page_range[0]}–{section.page_range[1]}
            </button>
          )}
          <span className="expand-indicator">{expanded ? '▾' : '▸'}</span>
        </div>
      </div>

      {section.importance_reason && (
        <div className="boq-section-reason">{section.importance_reason}</div>
      )}

      {expanded && (
        <div className="boq-section-details">
          {section.key_items && section.key_items.length > 0 && (
            <div className="boq-key-items">
              <div className="key-items-label">Key items:</div>
              {section.key_items.map((ki: BoqKeyItem, i: number) => (
                <div key={i} className="key-item-row">
                  <span className="key-item-pos mono">{ki.position_number}</span>
                  <span className="key-item-desc">{ki.description}</span>
                  <span className="key-item-qty">
                    {ki.quantity} {ki.unit}
                  </span>
                  {ki.page && ki.file && (
                    <button
                      className="page-ref-link"
                      onClick={() => onNavigate(ki.file!, ki.page!)}
                    >
                      p.{ki.page}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          {section.warnings && section.warnings.length > 0 && (
            <div className="boq-section-warnings">
              {section.warnings.map((w, i) => (
                <div key={i} className="boq-section-warning">{w}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Existing components ───────────────────────────────────────────

function ProjectOverview({ report }: { report: ResearchReport | null }) {
  if (!report) {
    return (
      <div className="research-section">
        <h3>Project Overview</h3>
        <div className="research-overview-content">
          <div className="feed-placeholder">Waiting for research report...</div>
        </div>
      </div>
    );
  }

  const fields = [
    { label: 'Project', value: report.project_name },
    { label: 'Type', value: report.project_type },
    { label: 'Description', value: report.project_description },
    { label: 'Location', value: report.location_info },
    { label: 'Complexity', value: report.complexity_rating },
    { label: 'Completeness', value: report.document_completeness ? `${Math.round(report.document_completeness * 100)}%` : undefined },
    { label: 'Standards', value: report.applicable_standards?.join(', ') },
    { label: 'Manufacturers', value: report.manufacturers?.join(', ') },
    { label: 'Circuits', value: report.estimated_total_circuits ? `~${report.estimated_total_circuits}` : undefined },
    { label: 'BoQ Positions', value: report.boq_total_positions ? `${report.boq_total_positions}` : undefined },
  ];

  return (
    <div className="research-section">
      <h3>Project Overview</h3>
      <div className="research-overview-content">
        {fields
          .filter((f) => f.value)
          .map((f) => (
            <div key={f.label} className="overview-field">
              <span className="field-label">{f.label}</span>
              <span className="field-value">{f.value}</span>
            </div>
          ))}
        {report.electrical_scope && (
          <div className="overview-field">
            <span className="field-label">Scope</span>
            <span className="field-value field-text">{report.electrical_scope}</span>
          </div>
        )}
        {report.distribution_boards && report.distribution_boards.length > 0 && (
          <div className="overview-field">
            <span className="field-label">Dist. Boards</span>
            <div className="field-value">
              {report.distribution_boards.map((b, i) => (
                <div key={i} className="board-item">
                  {b.name}
                  {b.location && ` — ${b.location}`}
                  {b.estimated_circuits && ` (~${b.estimated_circuits} circuits)`}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PageTypes({ report }: { report: ResearchReport | null }) {
  const pts = report?.page_type_summary;
  if (!pts || Object.keys(pts).length === 0) {
    return (
      <div className="research-section">
        <h3>Page Types</h3>
      </div>
    );
  }

  const sorted = Object.entries(pts).sort((a, b) => b[1] - a[1]);
  const max = Math.max(...sorted.map((s) => s[1]));

  return (
    <div className="research-section">
      <h3>Page Types</h3>
      <div className="page-type-chart">
        {sorted.map(([type, count]) => (
          <div key={type} className="page-type-row">
            <span className="page-type-label">{type.replace(/_/g, ' ')}</span>
            <div className="page-type-bar-bg">
              <div
                className="page-type-bar"
                style={{ width: `${(count / max) * 100}%` }}
              />
            </div>
            <span className="page-type-count">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FileInventory({ report }: { report: ResearchReport | null }) {
  const files = report?.file_names;
  if (!files || files.length === 0) {
    return (
      <div className="research-section">
        <h3>File Inventory</h3>
        <div className="data-panel">
          <div className="feed-placeholder">No file inventory yet</div>
        </div>
      </div>
    );
  }

  return (
    <div className="research-section">
      <h3>File Inventory</h3>
      <div className="data-panel">
        <table className="data-table">
          <thead>
            <tr>
              <th>Suggested Name</th>
              <th>Original</th>
              <th>Pages</th>
              <th>Types</th>
              <th>Focus</th>
            </tr>
          </thead>
          <tbody>
            {files.map((f: FileNameMapping, i: number) => (
              <tr key={i}>
                <td className="file-suggested">{f.suggested || f.original}</td>
                <td className="text-dim">{f.original}</td>
                <td className="text-center">{f.page_count || ''}</td>
                <td className="text-dim">{f.doc_types?.join(', ') || ''}</td>
                <td className="text-dim">{f.surveyor_focus || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SurveyorBrief({ report, triage }: { report: ResearchReport | null; triage: any }) {
  const brief = (report as any)?.surveyor_brief as any | undefined;
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [previewPage, setPreviewPage] = useState<string | null>(null);
  if (!brief || !brief.files?.length) return null;

  // Build file → pages map from triage
  const filePages: Record<string, { page: number; doc_type: string; title: string }[]> = {};
  if (triage?.classifications) {
    for (const c of triage.classifications) {
      const f = c.file || c.filename;
      if (!filePages[f]) filePages[f] = [];
      filePages[f].push({ page: c.page, doc_type: c.doc_type, title: c.title || '' });
    }
  }

  const priorityColor: Record<string, string> = {
    high: '#f38ba8', medium: '#f9e2af', low: '#a6e3a1',
  };

  const toggle = (i: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const files = brief.files || [];

  return (
    <div className="research-section">
      <h3>Surveyor Brief</h3>
      {brief.project_summary && (
        <div className="brief-project-summary">{brief.project_summary}</div>
      )}
      {brief.general_watch_for?.length > 0 && (
        <div className="brief-general-watch">
          {brief.general_watch_for.map((w: string, i: number) => (
            <div key={i} className="brief-general-item">{w}</div>
          ))}
        </div>
      )}
      <div className="surveyor-brief">
        {(() => {
          // Split files into annotatable vs extraction
          const ANNOT_TYPES = new Set(['floor_plan_power','floor_plan_lighting','floor_plan_data',
            'detail_drawing','circuit_diagram','wiring_diagram','layout_plan']);
          const annotatableFiles: any[] = [];
          const extractionFiles: any[] = [];
          const triageByFile: Record<string, Set<string>> = {};
          if (triage?.classifications) {
            for (const c of triage.classifications) {
              const f = c.file || c.filename;
              if (!triageByFile[f]) triageByFile[f] = new Set();
              triageByFile[f].add(c.doc_type);
            }
          }
          for (const h of files) {
            const types = triageByFile[h.file] || new Set();
            const isAnnot = [...types].some(t => ANNOT_TYPES.has(t));
            if (isAnnot) annotatableFiles.push(h);
            else extractionFiles.push(h);
          }
          return (<>
            {annotatableFiles.length > 0 && (
              <div className="brief-divider">
                <span className="brief-divider-label">For Annotators</span>
                <span className="brief-divider-count">{annotatableFiles.length} documents</span>
              </div>
            )}
            {annotatableFiles.map((h: any) => {
              const i = files.indexOf(h);
              return renderBriefCard(h, i);
            })}
            {extractionFiles.length > 0 && (
              <div className="brief-divider extraction">
                <span className="brief-divider-label">Automated Extraction</span>
                <span className="brief-divider-count">{extractionFiles.length} documents</span>
              </div>
            )}
            {extractionFiles.map((h: any) => {
              const i = files.indexOf(h);
              return renderBriefCard(h, i);
            })}
          </>);
        })()}
      </div>
    </div>
  );

  function renderBriefCard(h: any, i: number) {
    return (
          <div key={i} className="brief-card" style={{ borderLeftColor: priorityColor[h.priority] || '#6c7086' }}>
            <div className="brief-header" onClick={() => toggle(i)} style={{ cursor: 'pointer' }}>
              <span className="brief-priority-dot" style={{ background: priorityColor[h.priority] || '#6c7086' }} />
              <span className="brief-zone">{h.file?.replace(/\.pdf$/i, '')}</span>
              <span className="brief-doctype">{h.document_type}</span>
              {h.estimated_items && <span className="brief-estimate">{h.estimated_items}</span>}
              <span className="brief-expand">{expanded.has(i) ? '\u25B2' : '\u25BC'}</span>
            </div>

            {expanded.has(i) && (<div className="brief-body">
              {h.what_to_count?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">What to count</span>
                  <div className="brief-tags">
                    {h.what_to_count.map((item: string, j: number) => (
                      <span key={j} className="brief-tag count">{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {h.key_equipment?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">Expected equipment</span>
                  <div className="brief-tags">
                    {h.key_equipment.map((item: string, j: number) => (
                      <span key={j} className="brief-tag equip">{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {h.watch_for?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">Watch for</span>
                  <ul className="brief-watch">
                    {h.watch_for.map((item: string, j: number) => (
                      <li key={j}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {h.boq_sections?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">BoQ cross-reference</span>
                  <div className="brief-tags">
                    {h.boq_sections.map((item: string, j: number) => (
                      <span key={j} className="brief-tag boq">{item}</span>
                    ))}
                  </div>
                </div>
              )}

              {h.page_notes?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">Page-specific notes</span>
                  <div className="brief-page-notes">
                    {h.page_notes.map((pn: any, j: number) => (
                      <div key={j} className="brief-page-note">
                        <span className="brief-page-range">p. {pn.pages}</span>
                        <span className="brief-page-content">{pn.content}</span>
                        {pn.hint && <span className="brief-page-hint">{pn.hint}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Page thumbnails */}
              {filePages[h.file]?.length > 0 && (
                <div className="brief-section">
                  <span className="brief-label">Pages ({filePages[h.file].length})</span>
                  <div className="brief-thumbs">
                    {filePages[h.file].map((pg) => {
                      const key = `${h.file}:${pg.page}`;
                      const isActive = previewPage === key;
                      return (
                        <div key={key} className={`brief-thumb ${isActive ? 'active' : ''}`}
                          onClick={() => setPreviewPage(isActive ? null : key)}>
                          <div className="brief-thumb-num">p.{pg.page}</div>
                          <div className={`brief-thumb-type dt-${pg.doc_type}`}>
                            {pg.doc_type.replace(/_/g, ' ')}
                          </div>
                          {pg.title && <div className="brief-thumb-title">{pg.title}</div>}
                        </div>
                      );
                    })}
                  </div>
                  {previewPage && previewPage.startsWith(h.file + ':') && (
                    <div className="brief-preview">
                      <img
                        src={withProjectQuery(`/api/page/thumbnail?file=${encodeURIComponent(h.file)}&page=${previewPage.split(':')[1]}`)}
                        alt={`Preview ${previewPage}`}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>)}
          </div>
    );
  }
}


function ZoneCards({ report }: { report: ResearchReport | null }) {
  const zones = report?.zones;
  if (!zones || zones.length === 0) {
    return (
      <div className="research-section">
        <h3>Zone Structure</h3>
        <div className="data-panel">
          <div className="feed-placeholder">No zones defined yet</div>
        </div>
      </div>
    );
  }

  return (
    <div className="research-section">
      <h3>Zone Structure</h3>
      <div className="data-panel zone-cards-grid">
        {zones.map((z: ResearchZone) => (
          <div key={z.zone_id} className="zone-card">
            <div className="zone-card-header">
              <span className="zone-id">{z.zone_id}</span>
              <span className="zone-name">{z.name}</span>
              {z.scope && <span className="zone-scope badge">{z.scope}</span>}
            </div>
            {z.description && <div className="zone-desc">{z.description}</div>}
            <div className="zone-meta">
              {z.page_count && <span>{z.page_count} pages</span>}
              {z.floor_levels && z.floor_levels.length > 0 && (
                <span>Floors: {z.floor_levels.join(', ')}</span>
              )}
              {z.distribution_boards && z.distribution_boards.length > 0 && (
                <span>Boards: {z.distribution_boards.join(', ')}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Missing Documents ─────────────────────────────────────────────

function MissingDocuments({ report }: { report: ResearchReport | null }) {
  const docs = report?.missing_documents;
  if (!docs || docs.length === 0) return null;

  return (
    <div className="research-section">
      <h3>
        Missing Documents
        <span className="section-count-badge">{docs.length}</span>
      </h3>
      <div className="missing-documents">
        {docs.map((d, i) => (
          <div key={i} className="missing-doc-item">{d}</div>
        ))}
      </div>
    </div>
  );
}

// ── Quality Notes ─────────────────────────────────────────────────

function QualityNotes({ report }: { report: ResearchReport | null }) {
  const notes = report?.quality_notes;
  if (!notes || notes.length === 0) return null;

  return (
    <div className="research-section">
      <h3>
        Quality Notes
        <span className="section-count-badge">{notes.length}</span>
      </h3>
      <div className="quality-notes">
        {notes.map((n, i) => (
          <div key={i} className="quality-note-item">{n}</div>
        ))}
      </div>
    </div>
  );
}

function PublishProgressBanner() {
  const publishProgress = useSSEStore((s) => s.skillProgress['publish']);
  const planProgress = useSSEStore((s) => s.skillProgress['publish_plan']);

  const progress: SkillProgress | undefined = publishProgress || planProgress;
  if (!progress || progress.processed >= progress.total) return null;

  const pct = Math.round((progress.processed / progress.total) * 100);

  return (
    <div className="publish-progress-banner">
      <div className="publish-progress-header">
        <span className="publish-progress-label">
          {publishProgress ? 'Publishing Research' : 'Publishing Plan'}
        </span>
        <span className="publish-progress-count">
          {progress.processed} / {progress.total}
        </span>
        <span className="publish-progress-pct">{pct}%</span>
      </div>
      {progress.detail && (
        <div className="publish-progress-detail">{progress.detail}</div>
      )}
      <div className="publish-progress-bar-bg">
        <div className="publish-progress-bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
